import { getAtRiskAppointments } from '@shared/core/events'
import type { GetProfileResponseDto } from '@shared/core/users'
import { addDays, startOfDay } from 'date-fns'
import { useContext, useMemo } from 'react'
import { PAUSED_REMINDERS_MODAL_ROW_LIMIT } from 'src/modules/alerts/alerts.constants'
import type { PausedRemindersAlertState } from 'src/modules/alerts/alerts.types'
import { DateContext } from 'src/modules/core/context'
import { eventPreviewFields } from 'src/modules/event/constants/event-params'
import { useEventList } from 'src/modules/event/hooks/use-event-list'
import { useNotSentRemindersCount } from 'src/modules/event/hooks/use-not-sent-reminders-count'

type UsePausedRemindersAlertStateOptions = {
  profile?: GetProfileResponseDto
  isSyncingEvents: boolean
}

// Same key as the appointment tabs' visible-range query. It fetches only when nothing
// is cached, so the dashboard adds no Google-backed list request of its own.
const READ_LOADED_RANGE_OPTIONS = {
  staleTime: Infinity,
  refetchOnMount: false,
  refetchOnWindowFocus: false,
} as const

export function usePausedRemindersAlertState({
  profile,
  isSyncingEvents,
}: UsePausedRemindersAlertStateOptions): PausedRemindersAlertState {
  const isPaused = Boolean(profile?.reminders_paused)
  const { selectedDate, daysCount, startDate, endDate } = useContext(DateContext)
  const notSent = useNotSentRemindersCount({ enabled: isPaused })
  const params = useMemo(
    () => ({ fields: eventPreviewFields, start_date: startDate, end_date: endDate }),
    [startDate, endDate],
  )
  const events = useEventList(params, { enabled: isPaused && !isSyncingEvents, ...READ_LOADED_RANGE_OPTIONS })
  const notSentSettled = hasSettled(notSent)
  const eventsSettled = hasSettled(events)

  return useMemo(() => {
    const start = startOfDay(selectedDate)
    const atRisk = getAtRiskAppointments({
      events: events.data ?? [],
      now: new Date(),
      visibleRange: { start, end: addDays(start, daysCount) },
      rowLimit: PAUSED_REMINDERS_MODAL_ROW_LIMIT,
    })

    return {
      isReady: isPaused && notSentSettled && eventsSettled,
      notSentCount: notSent.data?.not_sent_count ?? 0,
      atRisk,
    }
  }, [daysCount, events.data, eventsSettled, isPaused, notSent.data, notSentSettled, selectedDate])
}

// A request that gave up counts as ready: the modal falls back to its neutral
// copy, whereas an open gate that never closes blocks every alert campaign.
function hasSettled(query: { isSuccess: boolean; isError: boolean }): boolean {
  return Boolean(query.isSuccess || query.isError)
}
